import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import cn from 'classnames';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  FiChevronLeft,
  FiChevronRight,
  FiPlus,
  FiCalendar,
} from 'react-icons/fi';

import { Button } from '../../ComponnentsUI/common/Button';
import ConfirmModal from '../../ComponnentsUI/common/ConfirmModal';
import {
  appointmentService,
  appointmentStartsInSlot,
  buildDaySlotTimes,
  buildLocalDateTime,
  canModifyAppointment,
  dedupeAppointments,
  getApiErrorMessage,
  getAppointmentSlotSpan,
  parseAppointmentTime,
  type AppointmentRead,
} from '../../../helpers/api/appointment/appointment';
import { appointmentKeys } from '../../../helpers/api/appointment/appointment-query-keys';
import { usePreferenceUser, useMe } from '../../../helpers/api/users/users';
import { usePermissions } from '../../../helpers/acl/permissions';
import {
  canFilterStaffCalendar,
  usesPersonalCalendar,
} from '../../../helpers/acl/schedulingRoles';
import BookingWizardModal from '../../ComponnentsUI/BookingWizardModal/BookingWizardModal';
import WorklistPanel from '../../ComponnentsUI/WorklistPanel/WorklistPanel';
import AppointmentCard from '../../ComponnentsUI/Scheduling/AppointmentCard';
import AppointmentDetailsDrawer from '../../ComponnentsUI/Scheduling/AppointmentDetailsDrawer';
import SegmentedControl from '../../ComponnentsUI/Scheduling/SegmentedControl';
import { getAppointmentStatusVisual } from '../../ComponnentsUI/Scheduling/schedulingStatus';
import { useStartAppointmentExam } from '../../ComponnentsUI/Scheduling/useStartAppointmentExam';

const SLOT_ROW_HEIGHT = 72;

const SchedulingPage = () => {
  const { t } = useTranslation('Scheduling');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const pendingScrollAppointmentId = useRef<string | null>(null);
  const cancelInFlightRef = useRef<Set<string>>(new Set());
  const [highlightAppointmentId, setHighlightAppointmentId] = useState<string | null>(
    null
  );

  const { data: user } = useMe();
  const userId = user?.id || user?.data?.id;
  const personalCalendar = usesPersonalCalendar(user);
  const canFilterStaff = canFilterStaffCalendar(user);
  const { can } = usePermissions();
  const canWriteAppt = can('appointment.write');
  const { data: prefsResponse } = usePreferenceUser();
  const duration = prefsResponse?.data?.session_duration || 30;

  const [activeView, setActiveView] = useState<'calendar' | 'worklist'>('worklist');
  const [viewMode, setViewMode] = useState<'day' | 'week'>('day');
  const [viewDate, setViewDate] = useState(dayjs());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] =
    useState<AppointmentRead | null>(null);
  const [editingAppointment, setEditingAppointment] =
    useState<AppointmentRead | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<dayjs.Dayjs | null>(null);
  const [viewAssigneeId, setViewAssigneeId] = useState<string | null>(null);
  const [cancelTargetId, setCancelTargetId] = useState<string | null>(null);

  useEffect(() => {
    const dateParam = searchParams.get('date');
    if (dateParam && dayjs(dateParam, 'YYYY-MM-DD', true).isValid()) {
      const next = buildLocalDateTime(dateParam, '12:00');
      setViewDate(next);
    }

    const assigneeParam = searchParams.get('assignee');
    if (assigneeParam && canFilterStaff) {
      setViewAssigneeId(assigneeParam);
    }

    const appointmentParam = searchParams.get('appointment');
    if (appointmentParam) {
      pendingScrollAppointmentId.current = appointmentParam;
      setViewMode('day');
    }
  }, [searchParams, canFilterStaff]);

  const { data: assignees = [] } = useQuery({
    queryKey: appointmentKeys.bookableDoctors(),
    queryFn: appointmentService.getBookableDoctors,
    enabled: !!userId && canFilterStaff,
  });

  const agendaDoctorId = personalCalendar ? userId : viewAssigneeId ?? undefined;

  useEffect(() => {
    if (!userId) return;
    if (personalCalendar) {
      setViewAssigneeId(userId);
    }
  }, [userId, personalCalendar]);

  const range = useMemo(() => {
    const start = viewMode === 'day' ? viewDate : viewDate.startOf('week');
    const end = viewMode === 'day' ? viewDate : viewDate.endOf('week');
    return { start: start.format('YYYY-MM-DD'), end: end.format('YYYY-MM-DD') };
  }, [viewDate, viewMode]);

  const { data: appointmentsData, isLoading } = useQuery({
    queryKey: appointmentKeys.agenda(range.start, range.end, agendaDoctorId ?? 'all'),
    queryFn: () =>
      appointmentService.getDailyAgenda(range.start, range.end, agendaDoctorId),
    enabled: !!userId && (personalCalendar ? !!userId : true),
  });

  const assigneeNameById = useMemo(() => {
    const map = new Map<string, string>();
    assignees.forEach((a) => {
      const name = [a.first_name, a.last_name].filter(Boolean).join(' ').trim();
      map.set(a.id, name || a.email || t('staff'));
    });
    return map;
  }, [assignees, t]);

  const appointments = useMemo(() => {
    if (!appointmentsData) return [];
    const raw = Array.isArray(appointmentsData) ? appointmentsData : [];
    return dedupeAppointments(raw);
  }, [appointmentsData]);

  const dayAppointments = useMemo(
    () =>
      appointments.filter((a) =>
        parseAppointmentTime(a.start_time).isSame(viewDate, 'day')
      ),
    [appointments, viewDate]
  );

  useEffect(() => {
    const targetId = pendingScrollAppointmentId.current;
    if (!targetId || isLoading) return;

    const appointment = appointments.find((a) => String(a.id) === targetId);
    if (!appointment) return;

    const scrollToAppointment = () => {
      const el = document.getElementById(`appointment-${targetId}`);
      if (!el) return false;

      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightAppointmentId(targetId);
      pendingScrollAppointmentId.current = null;
      window.setTimeout(() => setHighlightAppointmentId(null), 3000);

      const date = parseAppointmentTime(appointment.start_time).format('YYYY-MM-DD');
      navigate(`/Scheduling?date=${date}`, { replace: true });
      return true;
    };

    if (scrollToAppointment()) return;
    const retryTimer = window.setTimeout(() => {
      scrollToAppointment();
    }, 150);
    return () => window.clearTimeout(retryTimer);
  }, [appointments, isLoading, navigate]);

  const showAssigneeOnCards = !personalCalendar && !viewAssigneeId;

  const findAppointmentsForSlot = useCallback(
    (slot: dayjs.Dayjs): AppointmentRead[] => {
      const slotEnd = slot.add(duration, 'minutes');
      return appointments.filter((app: AppointmentRead) => {
        if (!parseAppointmentTime(app.start_time).isSame(viewDate, 'day')) return false;
        return appointmentStartsInSlot(slot, slotEnd, app);
      });
    },
    [appointments, viewDate, duration]
  );

  const canAddAnotherInSlot = useCallback((slot: dayjs.Dayjs, slotApps: AppointmentRead[]) => {
    if (slot.isBefore(dayjs(), 'minute')) return false;
    return slotApps.length > 0;
  }, []);

  const formatAssigneeLabel = (assignee: {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
  }) => {
    const name = [assignee.first_name, assignee.last_name].filter(Boolean).join(' ').trim();
    const base = name || assignee.email || t('staff');
    if (assignee.id === userId) return t('assigneeMe', { name: base });
    return t('assigneeProvider', { name: base });
  };

  const deleteMutation = useMutation({
    mutationFn: (id: string) => appointmentService.cancel(id),
    onSuccess: () => {
      message.success(t('messages.cancelSuccess'));
    },
    onError: (err: unknown) => {
      const status =
        (err as { status_code?: number; response?: { status?: number } })?.status_code ??
        (err as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        message.warning(t('messages.cancelNotFound'));
      } else {
        message.error(getApiErrorMessage(err, t('messages.cancelError')));
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.agendas() });
      queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() });
    },
  });

  const handleCancelAppointment = useCallback(
    (id: string) => {
      if (cancelInFlightRef.current.has(id) || deleteMutation.isPending) return;
      setCancelTargetId(id);
    },
    [deleteMutation.isPending]
  );

  const handleConfirmCancel = useCallback(() => {
    if (!cancelTargetId) return;
    if (cancelInFlightRef.current.has(cancelTargetId) || deleteMutation.isPending) {
      return;
    }
    const id = cancelTargetId;
    setCancelTargetId(null);
    cancelInFlightRef.current.add(id);
    deleteMutation.mutate(id, {
      onSettled: () => {
        cancelInFlightRef.current.delete(id);
      },
    });
  }, [cancelTargetId, deleteMutation]);

  const { startExam: handleStartExam, examFlowModals } =
    useStartAppointmentExam();

  const handleAppointmentCreated = useCallback(() => {
    setIsModalOpen(false);
    setEditingAppointment(null);
    setSelectedSlot(null);
    queryClient.invalidateQueries({ queryKey: appointmentKeys.agendas() });
    queryClient.invalidateQueries({ queryKey: appointmentKeys.lists() });
  }, [queryClient]);

  const openBooking = (slot: dayjs.Dayjs | null = null) => {
    setEditingAppointment(null);
    setSelectedSlot(slot);
    setIsModalOpen(true);
  };

  const openEditAppointment = useCallback((app: AppointmentRead) => {
    if (!canModifyAppointment(app)) {
      message.warning(
        t('messages.cannotModifyLocked', {
          defaultValue:
            'This appointment cannot be modified while it is in progress or finished.',
        })
      );
      return;
    }
    setSelectedAppointment(null);
    setSelectedSlot(null);
    setEditingAppointment(app);
    setIsModalOpen(true);
  }, [t]);

  const closeBookingModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingAppointment(null);
    setSelectedSlot(null);
  }, []);

  const timeSlots = useMemo(
    () => buildDaySlotTimes(viewDate.format('YYYY-MM-DD'), 8, 19, duration),
    [viewDate, duration]
  );

  const stats = useMemo(() => {
    const config = [
      {
        id: 't1',
        label: t('stats.firstTrimester'),
        color: 'bg-indigo-500',
        text: 'text-indigo-600',
      },
      {
        id: 't2',
        label: t('stats.secondTrimester'),
        color: 'bg-purple-500',
        text: 'text-purple-600',
      },
      {
        id: 't3',
        label: t('stats.thirdTrimester'),
        color: 'bg-blue-500',
        text: 'text-blue-600',
      },
      {
        id: 'emergency',
        label: t('stats.urgentEmergency'),
        color: 'bg-red-500',
        text: 'text-red-600',
      },
    ];
    return config.map((type) => ({
      ...type,
      count: dayAppointments.filter(
        (a) => String(a.appointment_type || '').toLowerCase() === type.id
      ).length,
    }));
  }, [dayAppointments, t]);

  const stepUnit =
    activeView === 'worklist' ? 'day' : viewMode === 'week' ? 'week' : 'day';

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden bg-slate-50 font-sans dark:bg-gray-900">
      <div className="px-4 pt-4 sm:px-6">
        <header className="shrink-0 border-b border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-800 sm:px-9">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-white md:text-2xl">
                  {t('title')}
                </h1>
                <span className="hidden text-sm font-medium text-slate-500 dark:text-gray-400 sm:inline">
                {activeView === 'worklist' || viewMode === 'day'
                  ? viewDate.format('dddd, D MMMM YYYY')
                  : t('weekRange', {
                      start: viewDate.startOf('week').format('D MMM'),
                      end: viewDate.endOf('week').format('D MMM YYYY'),
                    })}
                </span>
              </div>
              <p className="mt-0.5 text-xs text-slate-400">
                {personalCalendar
                  ? t('myCalendarOnly')
                  : t('workspace')}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <SegmentedControl
              aria-label={t('actions.scheduleView', {
                defaultValue: 'Scheduling view',
              })}
              value={activeView}
              onChange={setActiveView}
              options={[
                { value: 'worklist', label: t('worklist.tab') },
                { value: 'calendar', label: t('calendar.tab') },
              ]}
              size="md"
            />

            {canFilterStaff && (
              <select
                value={viewAssigneeId ?? ''}
                onChange={(e) => setViewAssigneeId(e.target.value || null)}
                aria-label={t('viewCalendar')}
                className="h-9 min-w-[160px] rounded-lg border border-gray-200 bg-white px-3 text-xs font-medium text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:[color-scheme:dark]"
              >
                <option value="">{t('allStaff')}</option>
                {assignees.map((assignee) => (
                  <option key={assignee.id} value={assignee.id}>
                    {formatAssigneeLabel(assignee)}
                  </option>
                ))}
              </select>
            )}

            <div className="flex items-center rounded-lg border border-gray-200 bg-white p-0.5 dark:border-gray-600 dark:bg-gray-700">
              <button
                type="button"
                aria-label={t('actions.prev', { defaultValue: 'Previous' })}
                onClick={() => setViewDate(viewDate.subtract(1, stepUnit))}
                className="rounded-md p-2 text-slate-500 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:hover:bg-gray-600"
              >
                <FiChevronLeft size={16} />
              </button>
              <button
                type="button"
                onClick={() => setViewDate(dayjs())}
                className="px-3 py-1.5 text-xs font-semibold uppercase text-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:text-blue-400"
              >
                {t('today')}
              </button>
              <button
                type="button"
                aria-label={t('actions.next', { defaultValue: 'Next' })}
                onClick={() => setViewDate(viewDate.add(1, stepUnit))}
                className="rounded-md p-2 text-slate-500 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:hover:bg-gray-600"
              >
                <FiChevronRight size={16} />
              </button>
            </div>

            {canWriteAppt && (
              <>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={viewDate.isBefore(dayjs(), 'day')}
                  onClick={() => openBooking(null)}
                  className="gap-1.5"
                >
                  <FiPlus strokeWidth={3} size={14} />
                  {t('newAppointment')}
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="shrink-0 border-b border-gray-200 bg-white px-4 py-2 dark:border-gray-700 dark:bg-gray-800 sm:px-9">
        <div className="flex items-center gap-2 overflow-x-auto pb-0.5">
          {Array.from({ length: 7 }).map((_, index) => {
            const day = viewDate.startOf('week').add(index, 'day');
            const selected = day.isSame(viewDate, 'day');
            const count = appointments.filter((app) =>
              parseAppointmentTime(app.start_time).isSame(day, 'day')
            ).length;
            return (
              <button
                key={day.toISOString()}
                type="button"
                onClick={() => setViewDate(day)}
                className={cn(
                  'flex min-w-[76px] items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500',
                  selected
                    ? 'border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                    : 'border-gray-200 bg-white text-slate-600 hover:border-blue-300 hover:bg-blue-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600'
                )}
              >
                <span>
                  <span className="block text-[10px] font-semibold uppercase opacity-70">
                    {day.format('ddd')}
                  </span>
                  <span className="block text-base font-bold leading-tight">
                    {day.format('D')}
                  </span>
                </span>
                {count > 0 && (
                  <span
                    className={cn(
                      'rounded-full px-1.5 py-0.5 text-[10px] font-bold',
                      selected
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-800 dark:text-blue-200'
                        : 'bg-slate-100 text-slate-500 dark:bg-gray-600 dark:text-gray-300'
                    )}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
          <div className="ml-auto hidden items-center gap-2 xl:flex">
            {stats.map((stat) => (
              <div
                key={stat.id}
                className="min-w-[92px] rounded-xl border border-gray-200 bg-slate-50 px-3 py-2 dark:border-gray-600 dark:bg-gray-700"
              >
                <p className="truncate text-[9px] font-semibold uppercase tracking-wide text-slate-400">
                  {stat.label}
                </p>
                <p className={cn('mt-0.5 text-lg font-bold leading-none', stat.text)}>
                  {stat.count}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
      </div>

      <div className="flex min-h-0 flex-1 overflow-hidden px-4 py-4 sm:px-6 sm:pb-6">
        {activeView === 'calendar' ? (
          <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3 dark:border-gray-700">
            <div>
              <div className="flex items-center gap-2 text-base font-semibold text-slate-800 dark:text-gray-100">
                <FiCalendar className="text-blue-600" size={17} aria-hidden />
                {t('calendar.tab')}
              </div>
              <p className="mt-0.5 text-xs text-slate-400">
                {dayAppointments.length} {t('worklist.items')} · {duration} min slots
              </p>
            </div>
            <SegmentedControl
              aria-label={t('actions.dayWeek', { defaultValue: 'Day or week' })}
              value={viewMode}
              onChange={setViewMode}
              options={[
                { value: 'day', label: t('day') },
                { value: 'week', label: t('week') },
              ]}
            />
          </div>

          <div
            ref={scrollContainerRef}
            className="custom-scrollbar flex-1 overflow-x-auto overflow-y-auto"
          >
            {isLoading ? (
              <div className="p-16 text-center text-xs font-semibold uppercase tracking-wide text-slate-300 animate-pulse">
                {t('syncingAgenda')}
              </div>
            ) : viewMode === 'day' ? (
              <div className="min-w-[360px] divide-y divide-slate-100 dark:divide-gray-700 lg:min-w-0">
                {timeSlots.map((slot) => {
                  const slotApps = findAppointmentsForSlot(slot);
                  const isPast = slot.isBefore(dayjs(), 'minute');
                  const isCurrentSlot =
                    slot.isSame(dayjs(), 'day') &&
                    dayjs().isAfter(slot) &&
                    dayjs().isBefore(slot.add(duration, 'minutes'));
                  const rowSpan =
                    slotApps.length > 0
                      ? Math.max(
                          ...slotApps.map((app) =>
                            getAppointmentSlotSpan(app, duration)
                          )
                        )
                      : 1;
                  const rowHeight = SLOT_ROW_HEIGHT * Math.min(rowSpan, 3);

                  return (
                    <div
                      key={slot.toISOString()}
                      className={cn(
                        'group relative flex transition-colors',
                        !isPast && 'hover:bg-blue-50/30 dark:hover:bg-blue-900/10'
                      )}
                      style={{ minHeight: rowHeight }}
                    >
                      {isCurrentSlot && (
                        <div className="pointer-events-none absolute left-14 right-0 top-1/2 z-10 h-px bg-cyan-500 sm:left-[72px]">
                          <span className="absolute -left-1 -top-1 h-2.5 w-2.5 rounded-full bg-cyan-500 ring-4 ring-cyan-500/15" />
                        </div>
                      )}
                      <div className="w-16 shrink-0 border-r border-slate-100 px-2 py-3 text-right dark:border-gray-700 sm:w-20">
                        <span
                          className={cn(
                            'text-[13px] font-semibold',
                            isCurrentSlot
                              ? 'text-cyan-600 dark:text-cyan-400'
                              : 'text-slate-500 dark:text-slate-400'
                          )}
                        >
                          {slot.format('HH:mm')}
                        </span>
                      </div>
                      <div className="flex flex-1 items-stretch p-2 min-w-0">
                        {slotApps.length > 0 ? (
                          <div className="flex w-full min-w-0 flex-col gap-1.5">
                            {slotApps.map((app) => {
                              const status = getAppointmentStatusVisual(app, t);
                              return (
                                <AppointmentCard
                                  key={app.id}
                                  app={app}
                                  variant="day"
                                  highlight={
                                    highlightAppointmentId === String(app.id)
                                  }
                                  showAssignee={showAssigneeOnCards}
                                  canWrite={canWriteAppt}
                                  assigneeFallbackById={assigneeNameById}
                                  onStartExam={handleStartExam}
                                  onSelect={setSelectedAppointment}
                                  onCancel={
                                    canWriteAppt
                                      ? handleCancelAppointment
                                      : undefined
                                  }
                                  className={
                                    status.isLocked || isPast
                                      ? 'opacity-80'
                                      : undefined
                                  }
                                />
                              );
                            })}
                            {canWriteAppt &&
                              canAddAnotherInSlot(slot, slotApps) && (
                                <button
                                  type="button"
                                  onClick={() => openBooking(slot)}
                                  className="flex h-10 w-full items-center justify-center gap-2 rounded-lg border border-dashed border-blue-200 text-xs font-semibold text-blue-500 transition hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-blue-800 dark:hover:bg-blue-900/20"
                                >
                                  <FiPlus size={14} />
                                  {t('addAnotherPatient')}
                                </button>
                              )}
                          </div>
                        ) : (
                          <button
                            type="button"
                            disabled={isPast || !canWriteAppt}
                            onClick={() => openBooking(slot)}
                            className={cn(
                              'flex h-14 w-full items-center justify-center gap-2 self-center rounded-lg border border-dashed border-slate-200 text-xs font-semibold text-slate-400 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-gray-600',
                              isPast || !canWriteAppt
                                ? 'cursor-default opacity-0'
                                : 'hover:border-blue-300 hover:bg-blue-50/60 hover:text-blue-600 dark:hover:bg-blue-900/20'
                            )}
                          >
                            {!isPast && canWriteAppt && (
                              <>
                                <FiPlus size={14} />
                                {t('clickToAllocate')}
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="grid min-h-full min-w-[1120px] grid-cols-7 gap-px bg-slate-200 dark:bg-gray-700 xl:min-w-0">
                {Array.from({ length: 7 }).map((_, i) => {
                  const day = viewDate.startOf('week').add(i, 'days');
                  const dayApps = appointments.filter((a) =>
                    parseAppointmentTime(a.start_time).isSame(day, 'day')
                  );
                  const isToday = day.isSame(dayjs(), 'day');
                  const isPastDay = day.isBefore(dayjs(), 'day');
                  const suggestedTime = isToday
                    ? dayjs().add(1, 'hour').startOf('hour')
                    : day.hour(9).minute(0).second(0);

                  return (
                    <section
                      key={day.toISOString()}
                      className={cn(
                        'flex min-h-full min-w-0 flex-col bg-white dark:bg-gray-800',
                        isPastDay && 'bg-slate-50/70 dark:bg-gray-900/40',
                        isToday && 'relative z-10 ring-2 ring-inset ring-blue-500'
                      )}
                    >
                      <div
                        className={cn(
                          'sticky top-0 z-20 flex items-center justify-between border-b border-gray-200 bg-white/95 px-3 py-3 backdrop-blur dark:border-gray-700 dark:bg-gray-800/95',
                          isToday && 'border-blue-200 bg-blue-50/95 dark:bg-blue-900/20'
                        )}
                      >
                        <div>
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 dark:text-gray-400">
                            {day.format('ddd')}
                          </p>
                          <p
                            className={cn(
                              'mt-0.5 text-base font-bold text-slate-800 dark:text-gray-100',
                              isToday && 'text-blue-700 dark:text-blue-300'
                            )}
                          >
                            {day.format('D MMM')}
                          </p>
                        </div>
                        <span
                          className={cn(
                            'flex h-6 min-w-6 items-center justify-center rounded-full bg-slate-100 px-1.5 text-[10px] font-bold text-slate-500 dark:bg-gray-700 dark:text-gray-300',
                            isToday && 'bg-blue-600 text-white dark:bg-blue-600'
                          )}
                        >
                          {dayApps.length}
                        </span>
                      </div>

                      <div className="flex flex-1 flex-col gap-2 p-2.5">
                        {!isPastDay && canWriteAppt && (
                          <button
                            type="button"
                            onClick={() => openBooking(suggestedTime)}
                            className="flex h-9 w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-blue-300 bg-blue-50/50 text-xs font-semibold text-blue-600 transition hover:border-blue-500 hover:bg-blue-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-blue-700 dark:bg-blue-900/20 dark:text-blue-300"
                          >
                            <FiPlus size={13} />
                            {t('newAppointment')}
                          </button>
                        )}

                        {dayApps.length === 0 && (
                          <div className="flex min-h-28 flex-1 items-center justify-center rounded-lg border border-dashed border-slate-200 px-3 text-center text-[11px] font-medium text-slate-400 dark:border-gray-700">
                            {t('week.emptyDay', {
                              defaultValue: 'No appointments',
                            })}
                          </div>
                        )}

                        {dayApps.map((app) => (
                          <div
                            key={app.id}
                            className="min-w-0"
                          >
                            <AppointmentCard
                              app={app}
                              variant="week"
                              highlight={highlightAppointmentId === String(app.id)}
                              showAssignee={showAssigneeOnCards}
                              canWrite={canWriteAppt}
                              assigneeFallbackById={assigneeNameById}
                              onStartExam={handleStartExam}
                              onSelect={setSelectedAppointment}
                              onCancel={
                                canWriteAppt ? handleCancelAppointment : undefined
                              }
                            />
                          </div>
                        ))}
                      </div>
                    </section>
                  );
                })}
              </div>
            )}
          </div>
          </main>
        ) : (
          <main className="min-h-0 min-w-0 flex-1 overflow-hidden">
            <WorklistPanel
              date={viewDate.format('YYYY-MM-DD')}
              performingProviderId={
                // Same staff dropdown as calendar (All staff = no filter).
                // Personal-calendar users keep Facility/My in the panel itself.
                !personalCalendar && viewAssigneeId
                  ? String(viewAssigneeId)
                  : undefined
              }
              onSelectAppointment={openEditAppointment}
            />
          </main>
        )}
      </div>

      <BookingWizardModal
        isOpen={isModalOpen && canWriteAppt}
        onClose={closeBookingModal}
        initialDateTime={selectedSlot}
        initialAssigneeId={!personalCalendar ? viewAssigneeId : null}
        appointmentToEdit={editingAppointment}
        onSuccess={handleAppointmentCreated}
      />
      <AppointmentDetailsDrawer
        appointment={selectedAppointment}
        canWrite={canWriteAppt}
        onClose={() => setSelectedAppointment(null)}
        onStart={handleStartExam}
        onCancel={handleCancelAppointment}
        onModify={openEditAppointment}
      />
      <ConfirmModal
        isOpen={!!cancelTargetId}
        onClose={() => setCancelTargetId(null)}
        onConfirm={handleConfirmCancel}
        title={t('messages.cancelConfirmTitle', {
          defaultValue: 'Cancel this appointment?',
        })}
        content={t('messages.cancelConfirmBody', {
          defaultValue: 'This cannot be undone.',
        })}
        confirmText={t('cancel')}
        cancelText={t('modal.discard')}
        isDestructive
      />
      {examFlowModals}
    </div>
  );
};

export default SchedulingPage;
